import React, { useContext, useState } from "react"
import Header from "../../layouts/Header"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box } from "@mui/material"
import BackIcon from "../../components/BackIcon"
import { useNavigate } from "react-router-dom"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"
import { GlobalContext } from "../../contexts/GlobalContext"
import { useMutation, api } from "../../api"
import getIconComponent from "../utils/getIconComponent"
import CustomBackdrop from "../../components/CustomBackdrop"
import { useQueryClient } from "@tanstack/react-query"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../utils/reactTableActions/exportToolbarActions"
import ModalImportCSV from "../../components/CustomCSVImportButton"
import Swal from "sweetalert2" // Importación para el modal de confirmación[cite: 11]

// Estilos base estandarizados[cite: 11]
const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: {
    primary: {
      main: "#196C87",
    },
    secondary: {
      main: "#196C87",
    },
  },
})

const JefedeProductosIndex = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)
  const [openModal, setOpenModal] = useState(false)

  // Mutación para eliminar el registro[cite: 11]
  const { mutateAsync: SaveEliminacionJefedeProducto, isPending: isDeletingJefedeProducto } = useMutation({
    queryKey: ["isDeletingJefedeProducto"],
    fn: async (data) => {
      const response = await api.post("/JefedeProductos/eliminarJefedeProductos", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["JefedeProductos"] })
    },
  })

  // Función para manejar el modal de confirmación antes de eliminar[cite: 11]
  const handleDelete = async (row) => {
    const confirmacion = await Swal.fire({
      title: "¿Eliminar Jefe de Producto?",
      html: `<p>¿Está seguro que desea eliminar el Jefe de Producto <strong>${row.original.jefecodigo} - ${row.original.jefedescri}</strong>?</p>
             <p style="font-size: 0.9em; color: #666;">Esta acción no se puede deshacer.</p>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d32f2f",
      cancelButtonColor: "#196C87",
      reverseButtons: true,
    })

    if (confirmacion.isConfirmed) {
      try {
        await SaveEliminacionJefedeProducto(row.original)
      } catch (err) {
        console.error("Error eliminando el Jefe de Producto:", err)
      }
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Gestión de Jefes de Producto</b>
        </div>

        <CustomBackdrop isLoading={isDeletingJefedeProducto} />

        <Box className={StyledRoot}>
          {/* MODAL DE IMPORTACIÓN CSV[cite: 11] */}
          <ModalImportCSV
            open={openModal}
            onClose={() => setOpenModal(false)}
            templateFileName="Plantilla_JefesDeProductos.csv"
            fieldConfigs={{
              jefecodigo: { required: true, key: true },
              jefedescri: { required: true },
              jefestatus: { required: false },
              jefecomisiona: { required: false },
              usrcodigo: { required: false },
              ciacodigo2: { required: false },
              emcodemp: { required: false },
            }}
            maxFileSize={10 * 1024 * 1024}
            validateEndpoint="/JefedeProductos/validarJefedeProductosIMP"
            insertEndpoint="/JefedeProductos/insertarJefedeProductosIMP"
            onImportComplete={() => qc.invalidateQueries({ queryKey: ["JefedeProductos"] })}
          />

          {/* GRILLA PRINCIPAL[cite: 11] */}
          <CustomConditionalActionsTableServer
            endpoint="/JefedeProductos/getAllJefedeProductos"
            errorMsgFilterSearch="Error al cargar datos de Jefes de Producto"
            queryKeyModal="JefedeProductos"
            perPage={10}
            rowActionsWidthTable={120}
            rowActions={(row) => {
              const editarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "EDITAR",
              )
              const eliminarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "ELIMINAR",
              )

              const actions = []

              if (editarAction) {
                actions.push({
                  label: "Editar",
                  key: "EDITAR",
                  icon: getIconComponent(editarAction?.accnameicono, editarAction?.acctipoico),
                  onClick: (row) => navigate("editar", { state: row.original }),
                })
              }

              if (eliminarAction) {
                actions.push({
                  label: "Eliminar",
                  key: "ELIMINAR",
                  icon: getIconComponent(eliminarAction?.accnameicono, eliminarAction?.acctipoico),
                  onClick: (row) => handleDelete(row), // Llama al Modal de Confirmación[cite: 11]
                })
              }

              return actions
            }}
            topToolbarCustomActions={({ table, device }) => {
              const crearAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action.acccaption === "CREAR")
              const exportarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action.acccaption === "EXPORTAR",
              )
              const importarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action.acccaption === "IMPORTAR",
              )

              const toolbarActions = []
              if (crearAction) {
                toolbarActions.push({
                  label: crearAction?.acccaption,
                  key: crearAction?.acccaption,
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => navigate("crear"),
                })
              }
              if (exportarAction) {
                toolbarActions.push({
                  type: "dropdown",
                  label: exportarAction?.acccaption,
                  key: "exportarDropdown",
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ columns, data }) => {
                        const title = "Reporte de Jefes de Producto"
                        if (device === "sm") {
                          return handleExportDataPdfSMScreen(
                            columns,
                            data,
                            title,
                            `${title} ${new Date().toLocaleString()}`,
                          )
                        }
                        handleExportDataPdfLGScreen(
                          columns,
                          table.getCoreRowModel().rows,
                          title,
                          `${title} ${new Date().toLocaleString()}`,
                        )
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ data }) =>
                        handleAllExportDataCSV(data, `Jefes_De_Productos_${new Date().toLocaleString()}`),
                    },
                  ],
                })
              }
              if (importarAction) {
                toolbarActions.push({
                  label: importarAction?.acccaption || "Importar",
                  key: "importarBtn",
                  icon: getIconComponent(
                    importarAction?.accnameicono || "UploadFile",
                    importarAction?.acctipoico || "MaterialIcons",
                  ),
                  onClick: () => setOpenModal(true),
                })
              }
              return toolbarActions
            }}
            columnsTable={[
              {
                accessorKey: "jefecodigo",
                header: "Código",
                size: 100,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "jefedescri",
                header: "Descripción",
                size: 250,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "jefecomisiona",
                header: "Comisiona",
                size: 120,
                Cell: ({ cell }) => {
                  const valor = cell.getValue()
                  return <span>{parseInt(valor) === 1 ? "SÍ" : "NO"}</span>
                },
              },
              {
                accessorKey: "usrcodigo",
                header: "Cód. Usuario",
                size: 120,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "jefestatus",
                header: "Estado",
                size: 100,
                Cell: ({ cell }) => {
                  const valor = cell.getValue()
                  return <span>{valor === "A" ? "ACTIVO" : "INACTIVO"}</span>
                },
              },
              {
                accessorKey: "jefefecisys",
                header: "Fecha de Creación",
                size: 160,
                Cell: ({ cell }) => {
                  const value = cell.getValue()
                  return <span>{normalFormatDate(value)}</span>
                },
              },
              {
                accessorKey: "jefeusuisys",
                header: "Creado Por",
                size: 120,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
              {
                accessorKey: "jefefecmsys",
                header: "Fecha de Mod.",
                size: 160,
                Cell: ({ cell }) => {
                  const value = cell.getValue()
                  return <span>{normalFormatDate(value)}</span>
                },
              },
              {
                accessorKey: "jefeusumsys",
                header: "Modificado Por",
                size: 120,
                Cell: ({ cell }) => <span>{cell.getValue()}</span>,
              },
            ]}
          />
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default JefedeProductosIndex
